import React from 'react';

import { Link } from '@tanstack/react-router';

import markUrl from '@src/assets/autoorbit-mark.svg';
import { Button, buttonVariants } from '@src/components/ui/Button';
import { cn } from '@src/components/ui/utils';
import { useAuthentication } from '@src/context/hooks';

import { resolveAction } from './notFoundActions';
import { isConnectionError } from './systemStateStatus';


// Per-status copy. Subtext is deliberately generic for 403 (no hardcoded role)
// and the 500 connection variant swaps in when the error carries no response.
const STATES = {
  '404': {
    code: '404',
    label: 'Page not found',
    title: 'Page not found',
    subtext: 'The page you’re looking for doesn’t exist or has moved. '
      + 'Check the link or head back to your dashboard.',
  },
  '403': {
    code: '403',
    label: 'You don’t have access',
    title: 'You don’t have access',
    subtext: 'Your role can’t open this page. Financials and team settings '
      + 'are limited to Owners and Managers. Ask an admin to grant access.',
  },
  '500': {
    code: '500',
    label: 'Something went wrong',
    title: 'Something went wrong',
    subtext: 'An unexpected error occurred on our end. We’ve been notified '
      + 'automatically. Try again in a moment.',
    connectionSubtext: 'We couldn’t reach the server. Check your connection '
      + 'and try again in a moment.',
  },
};


/**
 * Full-viewport branded system error state for 403 / 404 / 500.
 *
 * One parametrized component drives all three states (AUTOORBIT-419): a brand
 * lockup top-left and a centered card with a big gradient status code, a
 * headline, muted subtext, and an auth-aware CTA. Mirrors the brand lockup from
 * the old `NotFound` and reads the synchronous `isAuthenticated` flag so the
 * primary action points signed-in users at the dashboard and everyone else at
 * login.
 *
 * - 404 & 403: a single primary CTA (auth-aware home).
 * - 500: primary "Reload page" (prefers the boundary `reset`, else a hard
 *   reload) plus a secondary auth-aware home. When the error carries no HTTP
 *   response the subtext swaps to connection-flavored copy.
 *
 * As a router/CatchBoundary fallback it can receive `{ error, reset }`; the
 * boundary maps the error's status to `status` upstream (`ErrorBoundaryState`).
 */
const SystemState = React.forwardRef((props, ref) => {
  const { status = 500, error = null, reset = null, className, ...restProps } = props;

  const state = STATES[status] || STATES[500];
  const { isAuthenticated } = useAuthentication();
  const action = resolveAction(isAuthenticated);

  const isFiveHundred = state.code === '500';
  const subtext = isFiveHundred && isConnectionError(error)
    ? state.connectionSubtext
    : state.subtext;

  const handleReload = () => {
    if (reset) {
      reset();
      return;
    }
    window.location.reload();
  };

  return (
    <div
      ref={ref}
      aria-label={state.label}
      className={cn(
        'relative flex min-h-screen w-full flex-col items-center justify-center bg-canvas p-6',
        className,
      )}
      role="region"
      {...restProps}
    >
      <div className="absolute left-6 top-6 flex items-center gap-2.5">
        <img alt="" src={markUrl} aria-hidden="true" className="size-[30px] rounded-[9px]" />
        <span className="text-lg font-semibold text-ink">AutoOrbit</span>
      </div>

      <div className="flex w-full max-w-[560px] flex-col items-center gap-3.5 text-center">
        <p
          className={cn(
            'text-brand-gradient font-bold leading-none',
            'text-[90px] tracking-[-3.5px] lg:text-[128px] lg:tracking-[-5px]',
          )}
        >
          {state.code}
        </p>

        <p className="text-[23px] font-semibold tracking-[-0.5px] text-ink lg:text-[30px] lg:tracking-[-0.8px]">
          {state.title}
        </p>

        <p className="max-w-[560px] text-[13.5px] leading-normal text-ink-muted lg:text-[15px]">
          {subtext}
        </p>

        <div
          className={cn(
            'flex w-full max-w-[326px] flex-col gap-2.5 pt-2',
            'lg:max-w-none lg:flex-row lg:flex-wrap lg:items-center lg:justify-center lg:gap-3 lg:pt-2.5',
          )}
        >
          {isFiveHundred
            ? (
              <>
                <Button variant="primary" className="w-full lg:w-auto" onClick={handleReload}>
                  Reload page
                </Button>

                {/* Style the Link with buttonVariants — this Button can't use
                    `asChild` (its loading slot breaks Radix Slot's single-child
                    rule). Mirrors NotFound / AcceptInvite. */}
                <Link
                  className={cn(buttonVariants({ variant: 'secondary' }), 'w-full lg:w-auto')}
                  to={action.to}
                >
                  {action.label}
                </Link>
              </>
            )
            : (
              <Link
                className={cn(buttonVariants({ variant: 'primary' }), 'w-full lg:w-auto')}
                to={action.to}
              >
                {action.label}
              </Link>
            )}
        </div>
      </div>
    </div>
  );
});

SystemState.displayName = 'SystemState';

export { SystemState };
export default SystemState;
